import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { hasRoles } from '../../permissions/api/hasRoles.js';
import { authenticatedMiddleware } from '../../permissions/middlewares/authenticatedMiddleware.js';
import {
  checkPermissionMiddleware,
  checkPermissionMiddlewareFunction,
} from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission, Role } from '../../permissions/roles.js';
import { UserListSelectionInterface } from '../../users/list/entities/UserListSelectionInterface.js';
import { Booking } from '../entities/Booking.js';
import { BookingReassignResult } from '../entities/BookingReassignResult.js';
import { BookingUpsertResult } from '../entities/BookingUpsertResult.js';
import { resolveChangeBillingPositionValuesForBooking } from '../server/resolver/resolveChangeBillingPositionValuesForBooking.js';
import { resolveEndAllActiveBookingsForGroup } from '../server/resolver/resolveEndAllActiveBookingsForGroup.js';
import { resolveEndBookingsForUserOnGroupID } from '../server/resolver/resolveEndBookingsForUserOnGroupID.js';
import { resolveReassignBookingUser } from '../server/resolver/resolveReassignBookingUser.js';
import { resolveRemoveActiveBookingsForGroup } from '../server/resolver/resolveRemoveActiveBookingsForGroup.js';
import { resolveRemoveBookings } from '../server/resolver/resolveRemoveBookings.js';
import { resolveAddBookingsForSelection } from '../server/resolver/resolveAddBookingsForSelection.js';
import { resolveSubscribeBookingsForSelection } from '../server/resolver/resolveSubscribeBookingsForSelection.js';
import { resolveUpsertBookings } from '../server/resolver/resolveUpsertBookings.js';

/**
 * Booking API, needs new logic. No two bookings on the same group should have overlapping runtimes
 */

const subscribeBookingsForSelection = registerSubscription({
  type: () => [Booking],
  operationName: 'subscribeBookingsForSelection',
  middlewares: [authenticatedMiddleware],
  params: [{ type: () => UserListSelectionInterface, name: 'listSelection' }],
  resolve: resolveSubscribeBookingsForSelection,
});

const endAllActiveBookingsForGroup = registerMutation({
  type: () => Int,
  operationName: 'endAllActiveBookingsForGroup',
  params: [
    { type: () => ID, name: 'groupID' },
    { type: () => Date, name: 'endDate' },
  ],
  middlewares: [checkPermissionMiddleware(Permission.EDIT_BOOKINGS)],
  resolve: resolveEndAllActiveBookingsForGroup,
});

const endBookingsForUserOnGroupID = registerMutation({
  type: () => Int,
  operationName: 'endBookingsForUserOnGroupID',
  params: [
    { type: () => ID, name: 'groupID' },
    { type: () => ID, name: 'userID' },
    { type: () => Date, name: 'endDate' },
  ],
  middlewares: [checkPermissionMiddleware(Permission.EDIT_BOOKINGS)],
  resolve: resolveEndBookingsForUserOnGroupID,
});

const upsertBookings = registerMutation({
  type: () => BookingUpsertResult,
  operationName: 'upsertBookings',
  params: [{ type: () => [Booking], name: 'bookings' }],
  middlewares: [checkPermissionMiddleware(Permission.EDIT_BOOKINGS)],
  resolve: resolveUpsertBookings,
});

const addBookingsForSelection = registerMutation({
  type: () => BookingUpsertResult,
  operationName: 'addBookingsForSelection',
  params: [
    { type: () => ID, name: 'groupID' },
    { type: () => UserListSelectionInterface, name: 'listSelection' },
  ],
  middlewares: [checkPermissionMiddleware(Permission.EDIT_BOOKINGS)],
  resolve: resolveAddBookingsForSelection,
});

const removeBookings = registerMutation({
  type: () => Int,
  operationName: 'removeBookings',
  params: [{ type: () => [ID], name: 'bookingIds' }],
  middlewares: [checkPermissionMiddleware(Permission.EDIT_BOOKINGS)],
  resolve: resolveRemoveBookings,
});

const reassignBookingUser = registerMutation({
  type: () => BookingReassignResult,
  operationName: 'reassignBookingUser',
  params: [
    { type: () => ID, name: 'bookingID' },
    { type: () => ID, name: 'userID' },
  ],
  middlewares: [checkPermissionMiddlewareFunction(user => hasRoles({ user, roles: [Role.ADMIN] }))],
  resolve: resolveReassignBookingUser,
});

const removeActiveBookingsForGroup = registerMutation({
  type: () => Int,
  operationName: 'removeActiveBookingsForGroup',
  params: [{ type: () => ID, name: 'groupId' }],
  middlewares: [checkPermissionMiddleware(Permission.EDIT_BOOKINGS)],
  resolve: resolveRemoveActiveBookingsForGroup,
});

@Entity()
export class ChangeBillingPositionValuesForBookingOptions extends AdornisEntity {
  static override _class = 'ChangeBillingPositionValuesForBookingOptions';

  @Field(() => ID)
  bookingId!: string;

  @Field(() => Int)
  amountInCents: Maybe<number>;
}

const changeBillingPositionValuesForBooking = registerMutation({
  type: () => Int,
  operationName: 'changeBillingPositionValuesForBooking',
  params: [{ type: () => ChangeBillingPositionValuesForBookingOptions, name: 'options' }],
  middlewares: [checkPermissionMiddleware(Permission.EDIT_BOOKINGS)],
  resolve: resolveChangeBillingPositionValuesForBooking,
});

export const BookingAPI = {
  upsert: upsertBookings,
  addForSelection: addBookingsForSelection,
  remove: removeBookings,
  removeForGroup: removeActiveBookingsForGroup,
  reassignUser: reassignBookingUser,
  subscribeForSelection: subscribeBookingsForSelection,

  endAllActive: endAllActiveBookingsForGroup,
  endForUserOnGroup: endBookingsForUserOnGroupID,

  changeBpValues: changeBillingPositionValuesForBooking,
};
